// ./src/components/features/Authentication/BirthMonthYearFields/BirthMonthYearFields.tsx
//
// MONTH AND YEAR OF BIRTH, asked at registration where the brand has a minimum age (ms_auth `space.min_signup_age` — 16
// for eventsmagic and partylabz; owner, 2026-10-14). Month and year only: enough for an age rule, less than a birthday.
// ⓘ The SERVER decides the age (conservatively — someone born this month is not a year older yet); this only asks.
//   Its refusal arrives on `birthYear` / `birthMonth` and is shown here.
// ⚠ A NEUTRAL question (FTC, COPPA FAQ): every year can be chosen, and the minimum age is not announced before the answer —
//   a warning invites a false one, and a year list that stops at the minimum, or an "I am over 16" box, would not count.
import React, { useMemo } from "react";
import { Group, Input } from "@mantine/core";
import dayjs from "dayjs";

import { SelectField } from "@/components/generic/form";
import { YearSelector } from "@/components/generic/YearSelector";

export interface BirthMonthYearFieldsProps {
  month: number | null;
  year: number | null;
  onChange: (next: { month: number | null; year: number | null }) => void;
  required?: boolean;
  disabled?: boolean;
  monthError?: React.ReactNode;
  yearError?: React.ReactNode;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: dayjs().month(i).format("MMMM") }));

export const BirthMonthYearFields: React.FC<BirthMonthYearFieldsProps> = ({
  month,
  year,
  onChange,
  required,
  disabled,
  monthError,
  yearError,
}) => {
  const currentYear = useMemo(() => dayjs().year(), []);

  return (
    <Input.Wrapper
      label="Date of birth"
      required={required}
      description="We ask only the month and year, and never show them to anyone."
      size="md"
    >
      <Group grow gap="sm" align="flex-start" mt={6}>
        <SelectField
          aria-label="Month of birth"
          placeholder="Month"
          data={MONTHS}
          value={month ? String(month) : null}
          onChange={(_name, v) => onChange({ month: v ? Number(v) : null, year })}
          disabled={disabled}
          error={monthError}
          size="md"
        />
        <YearSelector
          aria-label="Year of birth"
          placeholder="Year"
          value={year}
          onChange={(v) => onChange({ month, year: v })}
          minYear={currentYear - 110}
          maxYear={currentYear}
          searchable
          required={false}
          disabled={disabled}
          error={yearError}
          size="md"
        />
      </Group>
    </Input.Wrapper>
  );
};
